import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Gift, Coins, Crown, Copy, RefreshCw } from "lucide-react";

export default function AdminRewards({ password }) {
  const [creditEmail, setCreditEmail] = useState("");
  const [creditAmount, setCreditAmount] = useState("50");
  const [slotEmail, setSlotEmail] = useState("");
  const [slotQty, setSlotQty] = useState("1");
  const [promoCredits, setPromoCredits] = useState("50");
  const [promoSlots, setPromoSlots] = useState("0");
  const [promoCount, setPromoCount] = useState("1");
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadCodes = useCallback(() => {
    api.get(`/admin/promo-codes?admin_password=${encodeURIComponent(password)}`)
      .then((r) => setCodes(r.data.codes || []))
      .catch(() => {});
  }, [password]);

  useEffect(() => { loadCodes(); }, [loadCodes]);

  const addCredits = async () => {
    if (!creditEmail.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/rewards/add-credits?admin_password=${encodeURIComponent(password)}`, {
        email: creditEmail.trim(), credits: parseInt(creditAmount, 10) || 0
      });
      toast.success(`Done — ${r.data.name} now has ${r.data.new_balance} credits`);
      setCreditEmail("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const grantSlots = async () => {
    if (!slotEmail.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/rewards/grant-slots?admin_password=${encodeURIComponent(password)}`, {
        email: slotEmail.trim(), quantity: parseInt(slotQty, 10) || 1
      });
      toast.success(`Done — ${r.data.slots_granted} free $50 slot(s) granted to ${r.data.name}`);
      setSlotEmail("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const generateCodes = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/promo-codes?admin_password=${encodeURIComponent(password)}`, {
        credits: parseInt(promoCredits, 10) || 0,
        free_slots: parseInt(promoSlots, 10) || 0,
        count: parseInt(promoCount, 10) || 1
      });
      toast.success(`${r.data.codes.length} promo code(s) generated`);
      loadCodes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <div className="glass-card p-6 mt-8" data-testid="admin-rewards-section">
      <div className="flex items-center gap-3 mb-1">
        <Gift className="w-5 h-5 text-emerald-400" />
        <h2 className="text-xl font-semibold">Rewards & Promo Codes</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Add credits or free $50 offer slots to any account, or generate one-time promo codes preloaded with rewards. Members redeem codes in Settings → voucher section (or the gift code field at signup).
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <p className="font-medium mb-3 flex items-center gap-2"><Coins className="w-4 h-4 text-amber-400" /> Add credits to an account</p>
          <div className="space-y-2">
            <Input placeholder="member@email.com" value={creditEmail} onChange={(e) => setCreditEmail(e.target.value)} data-testid="reward-credit-email" />
            <div className="flex gap-2">
              <Input type="number" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} className="w-28" data-testid="reward-credit-amount" />
              <Button onClick={addCredits} disabled={busy} data-testid="reward-credit-submit">Add credits</Button>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <p className="font-medium mb-3 flex items-center gap-2"><Crown className="w-4 h-4 text-violet-400" /> Grant free $50 offer slots</p>
          <div className="space-y-2">
            <Input placeholder="member@email.com" value={slotEmail} onChange={(e) => setSlotEmail(e.target.value)} data-testid="reward-slot-email" />
            <div className="flex gap-2">
              <Input type="number" value={slotQty} onChange={(e) => setSlotQty(e.target.value)} className="w-28" data-testid="reward-slot-qty" />
              <Button onClick={grantSlots} disabled={busy} data-testid="reward-slot-submit">Grant slots</Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
        <div className="flex items-center justify-between mb-3">
          <p className="font-medium flex items-center gap-2"><Gift className="w-4 h-4 text-emerald-400" /> One-time promo codes</p>
          <button onClick={loadCodes} className="text-muted-foreground hover:text-white" title="Refresh" data-testid="promo-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <div className="flex flex-wrap items-end gap-2 mb-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Credits</p>
            <Input type="number" value={promoCredits} onChange={(e) => setPromoCredits(e.target.value)} className="w-24" data-testid="promo-credits-input" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Free $50 slots</p>
            <Input type="number" value={promoSlots} onChange={(e) => setPromoSlots(e.target.value)} className="w-24" data-testid="promo-slots-input" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">How many codes</p>
            <Input type="number" value={promoCount} onChange={(e) => setPromoCount(e.target.value)} className="w-24" data-testid="promo-count-input" />
          </div>
          <Button onClick={generateCodes} disabled={busy} data-testid="promo-generate-btn">Generate</Button>
        </div>
        {codes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No promo codes yet.</p>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
            {codes.map((c) => (
              <div key={c.code} className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-black/20 border border-white/10" data-testid={`promo-row-${c.code}`}>
                <div className="min-w-0">
                  <code className="text-sm text-emerald-300">{c.code}</code>
                  <p className="text-xs text-muted-foreground">
                    {c.credits > 0 && `${c.credits} credits`}{c.credits > 0 && c.free_slots > 0 && " + "}{c.free_slots > 0 && `${c.free_slots} free slot${c.free_slots > 1 ? "s" : ""}`}
                  </p>
                </div>
                {c.redeemed_by ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-muted-foreground shrink-0">Used by {c.redeemed_by_name || "member"}</span>
                ) : (
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs shrink-0" onClick={() => copy(c.code)} data-testid={`promo-copy-${c.code}`}>
                    <Copy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
